import React, { useEffect, useMemo, useState } from 'react';
import { supabaseClient } from '../../config/supabase';
import { countSmsSegments } from '../../lib/communicationsCompose';
import { MESSAGE_TEMPLATE_HEADER_MEDIA_BUCKET } from '../../lib/messageTemplates';
import { formatPhone } from '../../lib/phone';

function isRemoteUrl(value = '') {
    return /^https?:\/\//i.test(String(value || ''));
}

function sampleForKey(variables = {}, key = '') {
    return variables?.[key]?.sample || '';
}

function renderVariableChip(key, variables, prefix) {
    const sample = sampleForKey(variables, key);
    if (sample) {
        return <span key={`${prefix}-value-${key}`} className="font-medium text-inherit">{sample}</span>;
    }
    return (
        <span
            key={`${prefix}-chip-${key}`}
            className="inline-flex rounded-md border border-amber-400/70 bg-amber-400/15 px-1.5 py-0.5 text-[0.72rem] font-semibold text-amber-200"
        >
            [{key}]
        </span>
    );
}

function renderRichText(value = '', variables = {}, prefix = 'rich') {
    const source = String(value || '');
    const richTokenPattern = /(\{\{\s*([^{}]+?)\s*\}\}|```([^`]+)```|\*([^*]+)\*|_([^_]+)_|~([^~]+)~)/g;
    const nodes = [];
    let lastIndex = 0;
    let matchIndex = 0;
    let match;
    while ((match = richTokenPattern.exec(source)) !== null) {
        if (match.index > lastIndex) nodes.push(source.slice(lastIndex, match.index));
        const [, token, variableKey, monoValue, boldValue, italicValue, strikeValue] = match;
        if (variableKey) {
            nodes.push(renderVariableChip(variableKey, variables, `${prefix}-${matchIndex}`));
        } else if (monoValue !== undefined) {
            nodes.push(
                <code
                    key={`${prefix}-${matchIndex}-mono`}
                    className="rounded bg-black/20 px-1 py-0.5 font-mono text-[0.82em]"
                >
                    {renderRichText(monoValue, variables, `${prefix}-${matchIndex}-mono-inner`)}
                </code>
            );
        } else if (boldValue !== undefined) {
            nodes.push(
                <strong key={`${prefix}-${matchIndex}-bold`}>
                    {renderRichText(boldValue, variables, `${prefix}-${matchIndex}-bold-inner`)}
                </strong>
            );
        } else if (italicValue !== undefined) {
            nodes.push(
                <em key={`${prefix}-${matchIndex}-italic`}>
                    {renderRichText(italicValue, variables, `${prefix}-${matchIndex}-italic-inner`)}
                </em>
            );
        } else if (strikeValue !== undefined) {
            nodes.push(
                <s key={`${prefix}-${matchIndex}-strike`}>
                    {renderRichText(strikeValue, variables, `${prefix}-${matchIndex}-strike-inner`)}
                </s>
            );
        } else {
            nodes.push(token);
        }
        lastIndex = match.index + token.length;
        matchIndex += 1;
    }
    if (lastIndex < source.length) nodes.push(source.slice(lastIndex));
    return nodes;
}

function buttonIcon(type = '') {
    if (type === 'phone') return '☎';
    if (type === 'url') return '↗';
    if (type === 'copy_code') return '⧉';
    return '•';
}

function buttonMeta(button = {}) {
    if (button.type === 'phone') return formatPhone(button.phone || '');
    if (button.type === 'url') return [button.url, button.dynamicSuffix].filter(Boolean).join('/');
    if (button.type === 'copy_code') return button.code || '';
    return '';
}

function normalizeMediaName(template = {}) {
    return template?.header?.file_name || template?.header?.storage_path?.split('/').pop() || 'attachment';
}

function HeaderPreview({ template, dark, imageUrl }) {
    const header = template?.header || { type: 'none' };
    if (header.type === 'text' && header.text) {
        return <p className="mb-2 text-sm font-semibold">{renderRichText(header.text, template?.variables, 'header')}</p>;
    }
    if (header.type === 'image') {
        if (imageUrl) {
            return <img alt="WhatsApp header preview" src={imageUrl} className="mb-2 h-28 w-full rounded-xl object-cover" />;
        }
        return (
            <div className={`mb-2 flex h-28 w-full items-center justify-center rounded-xl border text-xs ${dark ? 'border-gray-600 bg-gray-800 text-gray-300' : 'border-gray-200 bg-gray-100 text-gray-500'}`}>
                Image header preview
            </div>
        );
    }
    if (header.type === 'video') {
        return (
            <div className={`mb-2 flex h-28 w-full items-center justify-center rounded-xl border text-xs ${dark ? 'border-gray-600 bg-gray-800 text-gray-300' : 'border-gray-200 bg-gray-100 text-gray-500'}`}>
                ▶ Video header preview
            </div>
        );
    }
    if (header.type === 'document') {
        return (
            <div className={`mb-2 inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs ${dark ? 'border-gray-600 bg-gray-800 text-gray-200' : 'border-gray-200 bg-gray-100 text-gray-700'}`}>
                <span aria-hidden="true">📄</span>
                <span>{normalizeMediaName(template)}</span>
            </div>
        );
    }
    return null;
}

function WhatsAppPreview({ template, dark, orgName, imageUrl }) {
    return (
        <div className={`rounded-[28px] border p-3 ${dark ? 'border-gray-700 bg-[#0f1720]' : 'border-gray-200 bg-[#e9f6ef]'}`}>
            <div className={`mx-auto max-w-[280px] rounded-[22px] border px-3 py-2 shadow-sm ${dark ? 'border-gray-700 bg-[#1f2937] text-gray-100' : 'border-[#d9d9d9] bg-white text-gray-900'}`}>
                <p className={`text-[11px] font-semibold ${dark ? 'text-[#86efac]' : 'text-[#15803D]'}`}>{orgName}</p>
                <HeaderPreview template={template} dark={dark} imageUrl={imageUrl} />
                <div className="whitespace-pre-wrap break-words text-sm leading-6">
                    {renderRichText(template?.body || '', template?.variables, 'wa-body')}
                </div>
                {template?.footer && (
                    <p className={`mt-2 text-[11px] ${dark ? 'text-gray-400' : 'text-gray-500'}`}>
                        {renderRichText(template.footer, template?.variables, 'wa-footer')}
                    </p>
                )}
                <p className={`mt-2 text-right text-[10px] ${dark ? 'text-gray-500' : 'text-gray-400'}`}>10:24 AM</p>
            </div>
            {Array.isArray(template?.buttons) && template.buttons.length > 0 && (
                <div className="mx-auto mt-2 max-w-[280px] space-y-1.5">
                    {template.buttons.map((button, index) => (
                        <div
                            key={`${button.type || 'button'}-${index}`}
                            className={`flex items-center justify-between rounded-2xl border px-3 py-2 text-sm ${dark ? 'border-gray-700 bg-gray-900/90 text-gray-100' : 'border-white/70 bg-white/95 text-gray-800'}`}
                        >
                            <span className="flex items-center gap-2 font-medium">
                                <span aria-hidden="true">{buttonIcon(button.type)}</span>
                                <span>{button.text || 'Button text'}</span>
                            </span>
                            {buttonMeta(button) && (
                                <span className={`ml-3 max-w-[120px] truncate text-xs ${dark ? 'text-gray-400' : 'text-gray-500'}`}>
                                    {buttonMeta(button)}
                                </span>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function SmsPreview({ template, dark }) {
    const smsMetrics = countSmsSegments(template?.body || '');
    const warning = smsMetrics.segments > 3;
    return (
        <div className={`rounded-[28px] border p-3 ${dark ? 'border-gray-700 bg-[#0f1720]' : 'border-gray-200 bg-gray-50'}`}>
            <div className={`mx-auto max-w-[280px] rounded-[22px] border px-3 py-2 shadow-sm ${dark ? 'border-gray-700 bg-gray-800 text-gray-100' : 'border-gray-200 bg-white text-gray-900'}`}>
                <p className={`whitespace-pre-wrap break-words text-sm leading-6 ${dark ? 'text-gray-100' : 'text-gray-900'}`}>
                    {renderRichText(template?.body || '', template?.variables, 'sms-body')}
                </p>
                <p className={`mt-2 text-right text-[10px] ${dark ? 'text-gray-500' : 'text-gray-400'}`}>10:24 AM</p>
            </div>
            <div className={`mx-auto mt-2 max-w-[280px] rounded-2xl border px-3 py-2 text-xs ${warning ? (dark ? 'border-orange-500/60 bg-orange-950/40 text-orange-200' : 'border-orange-300 bg-orange-50 text-orange-800') : (dark ? 'border-gray-700 bg-gray-900/70 text-gray-300' : 'border-gray-200 bg-white text-gray-600')}`}>
                <p>
                    {smsMetrics.characters} characters · {smsMetrics.segments} {smsMetrics.segments === 1 ? 'text' : 'texts'} · {smsMetrics.encoding}
                </p>
                {smsMetrics.forcingUnicodeCharacters.length > 0 && (
                    <div className="mt-2 flex flex-wrap items-center gap-1">
                        <span className="font-medium">Unicode forced by:</span>
                        {smsMetrics.forcingUnicodeCharacters.map((character) => (
                            <span
                                key={`unicode-${character}`}
                                className={`inline-flex min-w-5 justify-center rounded-md px-1.5 py-0.5 font-semibold ${dark ? 'bg-orange-500/20 text-orange-200' : 'bg-orange-100 text-orange-700'}`}
                            >
                                {character}
                            </span>
                        ))}
                    </div>
                )}
                {warning && <p className="mt-2 font-medium">Warning: more than 3 SMS segments.</p>}
            </div>
        </div>
    );
}

function EmailPreview({ template, dark, orgName }) {
    return (
        <div className={`rounded-3xl border p-3 ${dark ? 'border-gray-700 bg-gray-900/70' : 'border-gray-200 bg-gray-50'}`}>
            <div className={`rounded-2xl border shadow-sm ${dark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
                <div className={`border-b px-4 py-3 text-xs ${dark ? 'border-gray-700 text-gray-300' : 'border-gray-200 text-gray-600'}`}>
                    <p><span className="font-semibold">From:</span> {orgName}</p>
                    <p className="mt-1">
                        <span className="font-semibold">Subject:</span>{' '}
                        {renderRichText(template?.emailSubject || 'Email subject preview', template?.variables, 'email-subject')}
                    </p>
                </div>
                <div className={`px-4 py-4 text-sm ${dark ? 'text-gray-100' : 'text-gray-800'}`}>
                    <div className="whitespace-pre-wrap break-words leading-6">
                        {renderRichText(template?.body || '', template?.variables, 'email-body')}
                    </div>
                </div>
            </div>
        </div>
    );
}

function TemplatePreview({
    template = null,
    theme = 'light',
    orgName = 'Your business',
    client = supabaseClient,
}) {
    const dark = theme === 'dark';
    const [imageUrl, setImageUrl] = useState('');
    const normalizedOrgName = useMemo(() => orgName || 'Your business', [orgName]);

    useEffect(() => {
        let active = true;
        const header = template?.header || {};
        if (header.type !== 'image' || !header.storage_path) {
            setImageUrl('');
            return () => { active = false; };
        }
        if (isRemoteUrl(header.storage_path)) {
            setImageUrl(header.storage_path);
            return () => { active = false; };
        }
        client.storage
            .from(MESSAGE_TEMPLATE_HEADER_MEDIA_BUCKET)
            .createSignedUrl(header.storage_path, 3600)
            .then(({ data }) => {
                if (active) setImageUrl(data?.signedUrl || '');
            })
            .catch(() => {
                if (active) setImageUrl('');
            });
        return () => { active = false; };
    }, [client, template?.header]);

    if (!template) return null;
    if (template.channel === 'whatsapp') {
        return <WhatsAppPreview template={template} dark={dark} orgName={normalizedOrgName} imageUrl={imageUrl} />;
    }
    if (template.channel === 'sms') {
        return <SmsPreview template={template} dark={dark} />;
    }
    return <EmailPreview template={template} dark={dark} orgName={normalizedOrgName} />;
}

export default TemplatePreview;
